import { useState } from "react";
import { ArrowRight, X, Plus, Check, Search } from "lucide-react";
import { CASINOS, PAYMENT_METHOD_NAMES, type CasinoData } from "@/lib/casinoData";

function parseWithdrawHours(w: string): number {
  const m = w.match(/(\d+)/);
  return m ? parseInt(m[1], 10) : 999;
}

function parseWager(w: string): number {
  const m = w.match(/(\d+)/);
  return m ? parseInt(m[1], 10) : 999;
}

function bestIdx(values: number[], mode: "max" | "min"): number[] {
  const target = mode === "max" ? Math.max(...values) : Math.min(...values);
  return values.reduce<number[]>((acc, v, i) => (v === target ? [...acc, i] : acc), []);
}

export function CasinoComparison() {
  const [selected, setSelected] = useState<CasinoData[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [search, setSearch] = useState("");

  const available = CASINOS.filter(c => !selected.find(s => s.id === c.id));
  const filtered = available.filter(c => c.name.toLowerCase().includes(search.toLowerCase()));

  function addCasino(casino: CasinoData) {
    if (selected.length < 5) {
      setSelected([...selected, casino]);
    }
    setDropdownOpen(false);
    setSearch("");
  }

  function removeCasino(id: number) {
    setSelected(selected.filter(c => c.id !== id));
  }

  const canCompare = selected.length >= 2;

  const bestRating = canCompare ? bestIdx(selected.map(c => c.rating), "max") : [];
  const bestBonus = canCompare ? bestIdx(selected.map(c => c.bonusValue), "max") : [];
  const bestDeposit = canCompare ? bestIdx(selected.map(c => c.minDeposit), "min") : [];
  const bestWithdraw = canCompare ? bestIdx(selected.map(c => parseWithdrawHours(c.withdrawTime)), "min") : [];
  const bestWager = canCompare ? bestIdx(selected.map(c => parseWager(c.wager)), "min") : [];

  return (
    <div data-testid="casino-comparison-tool">
      <div className="bg-white rounded-2xl border border-[#dde4ee] shadow-sm p-6 mb-6">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          {selected.map(casino => (
            <div
              key={casino.id}
              className="flex items-center gap-2 bg-[#f3f6fb] border border-[#dde4ee] rounded-lg px-3 py-2"
              data-testid={`selected-casino-${casino.id}`}
            >
              <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0">
                {casino.logo ? (
                  <img src={casino.logo} alt={casino.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white text-xs font-bold" style={{ background: casino.color }}>{casino.initials}</div>
                )}
              </div>
              <span className="font-semibold text-gray-900 text-sm">{casino.name}</span>
              <button
                onClick={() => removeCasino(casino.id)}
                className="text-gray-400 hover:text-red-500 transition-colors"
                data-testid={`remove-casino-${casino.id}`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}

          {selected.length < 5 && (
            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 border-2 border-dashed border-[#003da5]/30 text-[#003da5] rounded-lg px-4 py-2.5 font-semibold text-sm hover:border-[#003da5] hover:bg-[#f3f6fb] transition-colors"
                data-testid="button-add-casino"
              >
                <Plus className="w-4 h-4" />
                Přidat kasino
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-xl border border-[#dde4ee] shadow-lg z-50 max-h-80 overflow-hidden" data-testid="casino-dropdown">
                  <div className="p-3 border-b border-[#eef1f7]">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Vyberte kasino..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 text-sm border border-[#dde4ee] rounded-lg focus:outline-none focus:border-[#003da5]"
                        autoFocus
                        data-testid="input-search-casino"
                      />
                    </div>
                  </div>
                  <div className="overflow-y-auto max-h-56">
                    {filtered.length === 0 && (
                      <div className="px-4 py-3 text-sm text-gray-400">Žádné výsledky</div>
                    )}
                    {filtered.map(casino => (
                      <button
                        key={casino.id}
                        onClick={() => addCasino(casino)}
                        className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[#f3f6fb] transition-colors text-left"
                        data-testid={`dropdown-casino-${casino.id}`}
                      >
                        <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0">
                          {casino.logo ? (
                            <img src={casino.logo} alt={casino.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-white text-xs font-bold" style={{ background: casino.color }}>{casino.initials}</div>
                          )}
                        </div>
                        <span className="font-semibold text-gray-900 text-sm">{casino.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">
            Vybráno: <span className="font-bold text-[#003da5]">{selected.length}/5</span>
          </span>
          {!canCompare && selected.length > 0 && (
            <span className="text-xs text-amber-600 font-medium">Vyberte alespoň 2 kasina pro porovnání</span>
          )}
        </div>
      </div>

      {canCompare && (
        <div className="bg-white rounded-2xl border border-[#dde4ee] shadow-sm overflow-hidden" data-testid="comparison-table">
          <div className="overflow-x-auto">
            <table className="w-full" style={{ minWidth: `${200 + selected.length * 180}px` }}>
              <thead>
                <tr style={{ background: "linear-gradient(135deg, #001a4d 0%, #003da5 100%)" }}>
                  <th className="py-4 px-5 text-left text-white/60 text-xs font-semibold uppercase tracking-wider sticky left-0 z-10 min-w-[160px]" style={{ background: "linear-gradient(135deg, #001a4d 0%, #003da5 100%)" }}>
                    Parametr
                  </th>
                  {selected.map(casino => (
                    <th key={casino.id} className="py-4 px-4 text-center min-w-[160px]">
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-12 h-12 rounded-xl overflow-hidden border-2 border-white/20">
                          {casino.logo ? (
                            <img src={casino.logo} alt={casino.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-white text-sm font-bold" style={{ background: casino.color }}>{casino.initials}</div>
                          )}
                        </div>
                        <span className="text-white font-bold text-sm">{casino.name}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <Row label="Hodnocení">
                  {selected.map((c, i) => (
                    <td key={c.id} className="px-4 py-3.5 text-center" data-testid={`cell-rating-${c.id}`}>
                      <span className={`font-black text-lg ${bestRating.includes(i) ? "text-green-600" : "text-[#003da5]"}`}>
                        {c.rating}
                      </span>
                      <span className="text-gray-400 text-sm">/10</span>
                      {bestRating.includes(i) && <BestBadge />}
                    </td>
                  ))}
                </Row>
                <Row label="Bonus" alt>
                  {selected.map((c, i) => (
                    <td key={c.id} className="px-4 py-3.5 text-center" data-testid={`cell-bonus-${c.id}`}>
                      <span className={`font-bold text-sm ${bestBonus.includes(i) ? "text-green-600" : "text-gray-900"}`}>
                        {c.bonus}
                      </span>
                      {bestBonus.includes(i) && <BestBadge />}
                    </td>
                  ))}
                </Row>
                <Row label="Free Spiny">
                  {selected.map(c => (
                    <td key={c.id} className="px-4 py-3.5 text-center font-semibold text-gray-800 text-sm">
                      {c.freeSpins}
                    </td>
                  ))}
                </Row>
                <Row label="Protočení" alt>
                  {selected.map((c, i) => (
                    <td key={c.id} className="px-4 py-3.5 text-center">
                      <span className={`font-semibold text-sm ${bestWager.includes(i) ? "text-green-600" : "text-gray-800"}`}>
                        {c.wager}
                      </span>
                      {bestWager.includes(i) && <BestBadge />}
                    </td>
                  ))}
                </Row>
                <Row label="Licence">
                  {selected.map(c => (
                    <td key={c.id} className="px-4 py-3.5 text-center text-gray-700 text-sm font-medium">
                      {c.license}
                    </td>
                  ))}
                </Row>
                <Row label="Min. vklad" alt>
                  {selected.map((c, i) => (
                    <td key={c.id} className="px-4 py-3.5 text-center" data-testid={`cell-min-deposit-${c.id}`}>
                      <span className={`font-semibold text-sm ${bestDeposit.includes(i) ? "text-green-600" : "text-gray-800"}`}>
                        {c.minDeposit} Kč
                      </span>
                      {bestDeposit.includes(i) && <BestBadge />}
                    </td>
                  ))}
                </Row>
                <Row label="Doba výběru">
                  {selected.map((c, i) => (
                    <td key={c.id} className="px-4 py-3.5 text-center">
                      <span className={`font-semibold text-sm ${bestWithdraw.includes(i) ? "text-green-600" : "text-gray-800"}`}>
                        {c.withdrawTime}
                      </span>
                      {bestWithdraw.includes(i) && <BestBadge />}
                    </td>
                  ))}
                </Row>
                <Row label="Platební metody" alt>
                  {selected.map(c => (
                    <td key={c.id} className="px-4 py-3.5 text-center">
                      <div className="flex flex-wrap justify-center gap-1">
                        {c.paymentMethods.slice(0, 5).map(slug => (
                          <span key={slug} className="inline-block bg-[#f3f6fb] border border-[#dde4ee] text-gray-700 text-[10px] font-medium px-2 py-0.5 rounded-full">
                            {PAYMENT_METHOD_NAMES[slug] || slug}
                          </span>
                        ))}
                        {c.paymentMethods.length > 5 && (
                          <span className="inline-block text-gray-400 text-[10px] font-medium px-1">
                            +{c.paymentMethods.length - 5}
                          </span>
                        )}
                      </div>
                    </td>
                  ))}
                </Row>
                <Row label="Mobilní verze">
                  {selected.map(c => (
                    <td key={c.id} className="px-4 py-3.5 text-center" data-testid={`cell-mobile-${c.id}`}>
                      {c.mobileApp ? (
                        <span className="inline-flex items-center gap-1 text-green-600 font-semibold text-sm">
                          <Check className="w-4 h-4" /> Ano
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-red-400 font-semibold text-sm">
                          <X className="w-4 h-4" /> Ne
                        </span>
                      )}
                    </td>
                  ))}
                </Row>
                <tr className="border-t border-[#dde4ee]" style={{ background: "linear-gradient(135deg, #f8faff 0%, #f3f6fb 100%)" }}>
                  <td className="px-5 py-4 font-bold text-gray-900 text-sm sticky left-0 z-10" style={{ background: "linear-gradient(135deg, #f8faff 0%, #f3f6fb 100%)" }} />
                  {selected.map(c => (
                    <td key={c.id} className="px-4 py-4 text-center">
                      <a href={c.ctaUrl} target="_blank" rel="sponsored noopener noreferrer">
                        <button
                          className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg font-bold text-sm text-white transition-colors"
                          style={{ background: "#003da5" }}
                          onMouseEnter={e => (e.currentTarget.style.background = "#002d7c")}
                          onMouseLeave={e => (e.currentTarget.style.background = "#003da5")}
                          data-testid={`button-compare-hraj-${c.id}`}
                        >
                          Hraj <ArrowRight className="w-3.5 h-3.5" />
                        </button>
                      </a>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, children, alt }: { label: string; children: React.ReactNode; alt?: boolean }) {
  return (
    <tr className={`border-t border-[#eef1f7] ${alt ? "bg-[#fafbfe]" : "bg-white"}`}>
      <td className={`px-5 py-3.5 font-bold text-gray-900 text-sm sticky left-0 z-10 ${alt ? "bg-[#fafbfe]" : "bg-white"}`}>
        {label}
      </td>
      {children}
    </tr>
  );
}

function BestBadge() {
  return (
    <div className="mt-1">
      <span className="inline-block bg-green-100 text-green-700 text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
        Nejlepší
      </span>
    </div>
  );
}
